import _DatabaseModel, { i18n } from "./_DatabaseModel";
import { toSnakeCase } from "../mixins/HelperFunctions";
import {
  mdiAccountPlus,
  mdiAccountSwitch,
  mdiClipboardAlertOutline,
  mdiClipboardArrowDownOutline,
  mdiClipboardEditOutline,
  mdiClipboardOutline,
  mdiPriorityLow,
} from "@mdi/js";

const ISSUES_TAB_ASSETS = 85;
const ISSUES_TAB_DOCUMENTS = 86;
const ISSUES_TAB_LOCATIONS = 87;
const ISSUES_TAB_NOTES = 88;
const ISSUES_TAB_TASKS = 89;
const ISSUES_TAB_TIMELINE = 90;
const ISSUES_TAB_SERVICES = 168;
const ISSUES_TAB_LINKS = 172;

class Issue extends _DatabaseModel {
  static model = "issue";
  static showSize = 7;
  static cardComponent = "issue-card";
  static editDialogComponent = "issues-edit-dialog";
  static createNewObject = {
    object: { template_assignments: [], document_ids: [] },
    params: {},
  };

  static bottomNav = {
    mobile: ["card", "map", "cal"],
    desktop: ["card", "map", "cal"],
  };

  static tableHeaders = [
    { textKey: "form.label.id", value: "id", sortable: false },
    { textKey: "form.label.created_at", value: "created_at" },
    { textKey: "form.label.updated_at", value: "updated_at" },
    { textKey: "form.label.name", value: "name" },
    {
      textKey: "service.service",
      value: "current_issue_service",
      sortable: false,
    },
    { textKey: "sla.sla", value: "applied_sla", sortable: false },
    {
      textKey: "issue.label.assigned_person",
      value: "person",
      sortable: false,
    },
    { textKey: "form.label.tag_definitions", value: "tags", sortable: false },
    { textKey: "issue_priority.label.priority", value: "issue_priority_id" },
    { textKey: "issue_category.issue_category", value: "issue_category" },
    { textKey: "issue.label.status", value: "status" },
    { textKey: "form.label.feedback_rating", value: "feedback.rating" },
    { textKey: "issue.label.resolve_at", value: "resolve_at" },
    {
      textKey: "activity.list_label.created_by",
      value: "author",
      sortable: false,
    },
    { textKey: "asset.asset", value: "assets", sortable: false },
  ];

  static filter = {
    parts: {
      fields: [
        "name",
        "id",
        "issue_priority_id",
        "tag_definitions",
        "service_id",
        "status",
        "issue_category_id",
        "asset_id",
        "user_id",
        "public_token_valid",
        "persistence",
        "type",
      ],
      extended_fields: [
        "detail",
        "description",
        "solution",
        "note",
        "parent_id",
        "detail_on_asset",
        "person_relation_id",
        "author_id",
        "external_email_id",
        "rule_id",
        "automated_action_id",
        "customer_id",
        "service_organization_id",
        "price_list_id",
        "time_filter",
        "feedback_rating",
      ],
      time_filter: [
        "created_at",
        "updated_at",
        "planned_at",
        "resolve_at",
        "closed_at",
        "estimated_response_time",
        "responded_at",
        "estimated_resolution_time",
        "resolved_at",
      ],
    },
  };

  static statuses_config = {
    Incident: {
      new: ["assigned"],
      assigned: ["resolution"],
      resolution: ["resolved", "resolution_waiting"],
      resolved: ["closed", "resolution"],
      resolution_waiting: ["resolution"],
    },
    Request: {
      new: ["assigned"],
      assigned: ["resolution"],
      approval: ["resolution", "approval_waiting"],
      resolution: ["resolved", "resolution_waiting"],
      resolved: ["closed", "resolution"],
      approval_waiting: ["approval"],
      resolution_waiting: ["resolution"],
    },
    Change: {
      new: ["assigned"],
      assigned: ["assessment"],
      assessment: ["approval", "assessment_waiting"],
      approval: ["planning", "approval_waiting"],
      planning: ["resolution", "planning_waiting"],
      resolution: ["resolved", "testing", "resolution_waiting"],
      testing: ["resolved", "resolution", "deploying", "testing_waiting"],
      deploying: ["resolved", "deploying_waiting"],
      resolved: ["closed", "resolution"],
      approval_waiting: ["approval"],
      resolution_waiting: ["resolution"],
      assessment_waiting: ["assessment"],
      planning_waiting: ["planning"],
      testing_waiting: ["testing"],
      deploying_waiting: ["deploying"],
    },
    Problem: {
      new: ["assigned"],
      assigned: ["analysis"],
      analysis: ["resolution", "analysis_waiting"],
      resolution: ["resolved", "resolution_waiting"],
      resolved: ["closed", "resolution"],
      resolution_waiting: ["resolution"],
      analysis_waiting: ["analysis"],
    },
  };

  static operations = ["export", "report", "link_to_document", "tag", "untag"];

  static public_token_valid_options = {
    true: "filter.public_token_valid_levels.public",
    false: "filter.public_token_valid_levels.internal",
  };

  static persistence_options = {
    1: "filter.issue_closed_levels.closed",
    2: "filter.issue_closed_levels.all",
  };

  static issue_types = ["Incident", "Request", "Problem", "Change"];

  static feedback_ratings = [0, 1, 2, 3, 4, 5];

  static show = {
    buttons: ["edit"],
    reloadingModels: ["location", "task", "assets_issue", "issue_note"],
  };

  static basicInfo(object) {
    return [
      {
        labelKey: "issue.label.type",
        value: i18n.t(`issue.issue_types.${object?.type}`),
        type: "plain",
      },
      {
        labelKey: "service.service",
        value: object.current_issue_service?.service,
        type: "link",
        to: "services",
      },
      {
        labelKey: "issue_category.issue_category",
        value: object.issue_category?.name,
        type: "plain",
      },
      {
        labelKey: "issue.label.assigned_person",
        value: object.current_issue_service?.user?.person,
        type: "link",
        to: "persons",
      },
      {
        labelKey: "asset.assets",
        value: object.assets,
        type: "link_array",
        to: "assets",
      },
      {
        labelKey: "form.label.resolve_at",
        value: object.resolve_at,
        type: "date",
      },
      {
        labelKey: "issue.label.public_token_valid",
        value: object.public_token_valid,
        type: "public_link",
      },
      {
        labelKey: "form.label.author",
        value: object.author,
        type: "link",
        to: toSnakeCase(object.author_type, true),
      },
      {
        labelKey: "app_parts.feedback",
        value: object.feedback,
        type: "feedback",
        condition: !!object.feedback?.rating,
      },
      {
        labelKey: "form.label.updated_at",
        value: object.updated_at,
        type: "date_time",
      },
      {
        labelKey: "form.label.created_at",
        value: object.created_at,
        type: "date_time",
      },
      {
        labelKey: "form.label.closed_at",
        value: object.closed_at,
        type: "date_time",
        condition: object.closed_at,
      },
    ];
  }

  static submenuActions = [
    {
      action: "switchToService",
      icon: mdiClipboardArrowDownOutline,
      titleKey: "issue.label.switch_to_service",
      display: (object) =>
        object.permissions?.switch_to_service &&
        ["Incident"].includes(object.type),
    },
    {
      action: "correctIssue",
      icon: mdiClipboardEditOutline,
      titleKey: "issue.label.correct",
      display: (object) =>
        object.permissions?.correct &&
        ["Incident", "Request"].includes(object.type),
    },
    {
      action: "changePriority",
      icon: mdiPriorityLow,
      titleKey: "issue.label.change_priority",
      display: (object) =>
        object.permissions?.change_priority &&
        ["Incident", "Request"].includes(object.type),
    },
    {
      action: "changeAssignedPerson",
      icon: mdiAccountSwitch,
      titleKey: "issue.label.change_assigned_person",
      display: (object) =>
        object.permissions?.assign && object.current_issue_service?.user,
    },
    {
      action: "changeAssignedPerson",
      icon: mdiAccountPlus,
      titleKey: "issue.label.add_assigned_person",
      display: (object) =>
        object.permissions?.assign && !object.current_issue_service?.user,
    },
    this.commonActions.openActivities,
  ];

  static tabs = [
    {
      titleKey: "template.detail",
      component: "TemplatesDetailTab",
      disabled: this.defaultDisabledTab(),
    },
    {
      titleKey: "issue_note.issue_notes",
      permission: ISSUES_TAB_NOTES,
      component: "IssueNotesTab",
      disabled: (object) => !!object.closed_at,
    },
    {
      titleKey: "issue.label.tasks",
      permission: ISSUES_TAB_TASKS,
      component: "IssueTasksTab",
      disabled: (object) => !!object.closed_at,
    },
    {
      titleKey: "document.documents",
      permission: ISSUES_TAB_DOCUMENTS,
      component: "DocumentsTab",
      disabled: (object) => !!object.closed_at,
    },
    {
      titleKey: "asset.assets",
      permission: ISSUES_TAB_ASSETS,
      component: "AssetsIssuesTab",
      disabled: this.defaultDisabledTab(),
    },
    {
      titleKey: "issue.label.links",
      permission: ISSUES_TAB_LINKS,
      component: "IssueLinksTab",
      disabled: this.defaultDisabledTab(),
    },
    {
      titleKey: "issue.label.services",
      permission: ISSUES_TAB_SERVICES,
      component: "IssueServicesTab",
      disabled: this.defaultDisabledTab(),
    },
    {
      titleKey: "location.location",
      permission: ISSUES_TAB_LOCATIONS,
      component: "LocationsTab",
      parameters: { entity_type: "Issue" },
      disabled: this.defaultDisabledTab(),
    },
    {
      titleKey: "issue.label.timeline",
      permission: ISSUES_TAB_TIMELINE,
      component: "TimelineTab",
      disabled: this.defaultDisabledTab(),
    },
  ];

  static cardFields(object) {
    return {
      title: object.name,
      src: {
        cardIcon:
          object.type === "Incident"
            ? mdiClipboardAlertOutline
            : mdiClipboardOutline,
      },
    };
  }
}

export default Issue;
